import { WAREHOUSE_DELIVERY_OPTIONS } from 'constants/productColumns';
import { DELIVERY_OPTIONS } from 'constants/warehouseDeliveryOptions';
import Warehouse from 'data/Warehouse';
import Product, * as Prod from 'data/Product';
import Currency from 'data/Currency';
import { PRICE_TYPE } from 'constants/product';
import { COUNTRY_ISO } from 'constants/country';
import Variant from 'data/Variant';
import { getItems } from 'lib/SessionStorage';
import Country from 'data/Country';
import { STORAGE_KEYS } from 'constants/general';

export const parsePointOfServicePayload = (pos: any) => ({
  posId: pos.posId,
  nickname: pos.nickname,
  name: pos.name,
  code: pos.code,
  street: pos.street,
  city: pos.city,
  state: pos.state,
  zip: pos.zip,
  display: pos.display,
  latitude: pos.latitude,
  longitude: pos.longitude,
  stateIsoCode: pos.stateIsoCode,
  regionIsoCode: pos.regionIsoCode,
  countryIsoCode: pos.countryIsoCode,
  shortIsoCode: pos.shortIsoCode,
  countryId: Number(pos.country?.id),
});

export const mapDeliveryOption = (warehouse: Warehouse) => {
  if (warehouse.delivery && warehouse.pickup) {
    return DELIVERY_OPTIONS.SHIPPING_AND_PICKUP;
  } else if (warehouse.delivery) {
    return DELIVERY_OPTIONS.SHIPPING_ONLY;
  } else if (warehouse.pickup) {
    return DELIVERY_OPTIONS.PICKUP_ONLY;
  } else {
    return DELIVERY_OPTIONS.SHIPPING_AND_PICKUP;
  }
};

export const warehouseDeliveryMode = (warehouse: Prod.ProductWarehouse) => {
  let status;
  if (warehouse.delivery && warehouse.pickup) {
    return (status = WAREHOUSE_DELIVERY_OPTIONS.SHIPPING_AND_PICKUP);
  } else if (warehouse.delivery) {
    return (status = WAREHOUSE_DELIVERY_OPTIONS.SHIPPING_ONLY);
  } else if (warehouse.pickup) {
    return (status = WAREHOUSE_DELIVERY_OPTIONS.PICKUP_ONLY);
  } else {
    return (status = 'none');
  }
};

export const getUpdateVariantsOnWarehouseAdd = (
  product: Product,
  updatedWarehouses: any[],
  currencies: Currency[]
) => {
  // Pre-compute warehouse and country data outside the variant loop
  const warehouseData = updatedWarehouses.map(warehouse => ({
    id: warehouse.id,
    type: warehouse.type || PRICE_TYPE.PURCHASE,
    countryId: warehouse.address?.country?.id,
    countryIso: warehouse.address?.country?.countryIso
  }));

  // Create mapping of country IDs to currency IDs
  const countryCurrencyMap = new Map();
  currencies.forEach(currency => {
    if (currency.countryId) {
      countryCurrencyMap.set(currency.countryId, currency.id);
    }
  });

  // Pre-create empty rental price objects
  const emptyRentalPriceObj = { id: undefined, range: null, rangeUnits: 'Days', rangePrice: 0 };
  const emptyRentalPrices = Array.from({ length: 1 }, () => ({ ...emptyRentalPriceObj }));

  return product.variants.map((variant) => {
    // Create sets for tracking existing warehouse IDs and country IDs
    const existingWarehouseIds = new Set(
      variant.productStocks?.map(stock => `${stock.warehouseId}-${stock.type}`) || []
    );
    const existingCountryIds = new Set(
      variant.productVariantPrices?.map(price => price.countryId) || []
    );
    
    // Track if US/CA rental prices exist
    const hasUSRental = variant.rentalPriceUS && variant.rentalPriceUS.length > 0;
    const hasCARental = variant.rentalPriceCA && variant.rentalPriceCA.length > 0;

    // Process all warehouses at once
    for (const warehouse of warehouseData) {
      // Add product stock if not exists
      if (!existingWarehouseIds.has(`${warehouse.id}-${warehouse.type}`)) {
        variant.productStocks = variant.productStocks || [];
        variant.productStocks.push({
          warehouseId: warehouse.id,
          available: false,
          availableForPickup: product.availableForPickup,
          availableForShipping: product.shipping,
          postOrderShippingCost: product.postOrderShippingCost,
          deliveryLeadDays: product.deliveryLeadDays,
          type: warehouse.type
        });
      }

      // Add product variant price if needed
      if (warehouse.countryId && !existingCountryIds.has(warehouse.countryId)) {
        variant.productVariantPrices = variant.productVariantPrices || [];
        variant.productVariantPrices.push({
          countryId: warehouse.countryId,
          pricePerUnit: 0,
          itemsPerUnit: 1,
          productVariantId: variant.id,
          currencyId: countryCurrencyMap.get(warehouse.countryId)
        });
        existingCountryIds.add(warehouse.countryId);
      }

      // Add rental prices if needed
      if (warehouse.type === PRICE_TYPE.RENT) {
        if (warehouse.countryIso === COUNTRY_ISO.US && !hasUSRental) {
          variant.rentalPriceUS = JSON.parse(JSON.stringify(emptyRentalPrices));
        } else if (warehouse.countryIso === COUNTRY_ISO.CA && !hasCARental) {
          variant.rentalPriceCA = JSON.parse(JSON.stringify(emptyRentalPrices));
        }
      }
    }

    variant.dirty = true;
    return variant;
  });
};


export const getFieldNameForWarehouseColumn = (key: string) => {
  switch (key) {
    case 'available':
      return 'In stock'
    case 'deliveryLeadDays':
      return 'Delivery lead days'
    case 'postOrderShippingCost':
      return 'Post order shipping cost'
    default:
      return key
  }
};

export const getColumnMetaDataForWarehouse = (key: string, label: string, warehouse: any) => {
  return {
    key,
    label,
    isWarehouseColumn: true,
    warehouse: warehouse
  }
}

const getCountriesUniqueListFromWarehouses = (warehouses: Prod.ProductWarehouse[]) => {
  const countryIds = Array.from(
    new Set(warehouses.map(warehouse => warehouse?.address?.country.id))
  );
  return countryIds;
}


/**
 * Updates variants by filtering productVariantPrices based on available countries in warehouses.
 *
 * @param {Array} updatedVariants - The array of updated variants.
 * @param {Array} updatedWarehouses - The array of updated warehouses.
 * @returns {Array} - The updated variants with filtered productVariantPrices.
 */
export const updateVariantPricesWithAvailableCountries = (variants: Variant[], updatedWarehouses: Prod.ProductWarehouse[]) => {

  // Recompute the list of country IDs based on the updated warehouses

  const countryIds = getCountriesUniqueListFromWarehouses(updatedWarehouses);

  const updatedVariants = variants.map(variant => {
    const updatedProductVariantPrices = variant?.productVariantPrices?.filter(price =>
      countryIds.includes(price.countryId)
    );
    return {
      ...variant,
      productVariantPrices: updatedProductVariantPrices,
    };
  });

  return updatedVariants;
}


/**
 * Updates variants by filtering productVariantRanges based on available countries in warehouses.
 *
 * @param {Array} updatedVariants - The array of updated variants.
 * @param {Array} updatedWarehouses - The array of updated warehouses.
 * @returns {Array} - The updated variants with filtered productVariantPrices.
 */
export const updateVariantRangesWithAvailableCountries = (variants: Variant[], updatedWarehouses: Prod.ProductWarehouse[]) => {
  // Recompute the list of country IDs based on the updated warehouses

  const countryIds = getCountriesUniqueListFromWarehouses(updatedWarehouses);
  const countries = getItems(STORAGE_KEYS.COUNTRIES);

  // Find the specific countries for CA and US based on CountryIsoCode
  const canadaCountry = countries.find((country: Country) => country.countryIso === COUNTRY_ISO.CA);
  const usaCountry = countries.find((country: Country) => country.countryIso === COUNTRY_ISO.US);


  const updatedVariants = variants.map(variant => {
    const updatedRentalPriceCA = canadaCountry && countryIds.includes(canadaCountry.id)
      ? variant.rentalPriceCA
      : [];

    const updatedRentalPriceUS = usaCountry && countryIds.includes(usaCountry.id)
      ? variant.rentalPriceUS
      : []; // Empty array if no US country ID exists in countryIds

    return {
      ...variant,
      rentalPriceCA: updatedRentalPriceCA,
      rentalPriceUS: updatedRentalPriceUS,
    };
  });

  return updatedVariants;
}

export const getWarehousesByType = (warehouses: Warehouse[], type: string): Warehouse[] => {
  return warehouses.filter(warehouse => warehouse?.type === type || (!warehouse?.type && type === PRICE_TYPE.PURCHASE));
};